<template>
  <div class="settings-reservations">
    <sticky-header :title="$t('settingsReservations.title')">
      <btn
        type="success"
        slot="ui"
        @click.native="save">
        {{ $t('common.ui.save') }}
      </btn>

      <btn
        url="/settings"
        slot="ui">
        {{ $t('common.ui.back') }}
      </btn>
    </sticky-header>

    <block
      :title="$t('settingsReservations.configureReservationsTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field
        v-if="servicesEnabled"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.servicesLabel') }}
        </label>

        <input
          slot="field"
          v-model="serviceName[currentLanguages.service_name]" />

        <language-switcher
          slot="field"
          relatedTo="service_name" />
      </field>

      <field
        v-if="workersEnabled"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.allowWorkerSelect') }}
        </label>

        <switcher
          v-model="showWorkers"
          key="show-workers-switcher"
          slot="field" />
      </field>

      <field
        v-if="workersEnabled && servicesEnabled"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.syncAvailableHoursAccordingToAvailability') }}
        </label>

        <select
          slot="field"
          v-model="startCalendarBy">
          <option :value="0">
            {{ $t('settingsReservations.workers') }}
          </option>
          <option :value="1">
            {{ $t('settingsReservations.services') }}
          </option>
        </select>

        <small
          slot="note"
          class="note">
          {{ $t('settingsReservations.syncHoursDescription') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.timeSpaces') }}
        </label>

        <div slot="field">
          <div class="field-flex">
            <switcher
              :disabled="!servicesEnabled"
              key="time-spaces-the-same-as-service-duration"
              v-model="timeSpacesTheSameAsServiceDuration" />

            <small>{{ $t('settingsReservations.timeSpacesTheSameAsServiceDuration') }}</small>
          </div>

          <div v-if="!timeSpacesTheSameAsServiceDuration">
            <select
              :class="{ 'small-select': true }"
              v-model="timeSpaces.hours"
              name="time-spaces-hours">
              <option :value="0">0 h</option>
              <option :value="1">1 h</option>
              <option :value="2">2 h</option>
              <option :value="3">3 h</option>
              <option :value="4">4 h</option>
              <option :value="5">5 h</option>
              <option :value="6">6 h</option>
              <option :value="7">7 h</option>
              <option :value="8">8 h</option>
              <option :value="9">9 h</option>
              <option :value="10">10 h</option>
              <option :value="11">11 h</option>
              <option :value="12">12 h</option>
            </select>

            <select
              :class="{ 'small-select': true }"
              v-model="timeSpaces.minutes"
              name="time-spaces-minutes">
              <option :value="0">0 min</option>
              <option :value="5">5 min</option>
              <option :value="10">10 min</option>
              <option :value="15">15 min</option>
              <option :value="20">20 min</option>
              <option :value="25">25 min</option>
              <option :value="30">30 min</option>
              <option :value="35">35 min</option>
              <option :value="40">40 min</option>
              <option :value="45">45 min</option>
              <option :value="50">50 min</option>
              <option :value="55">55 min</option>
            </select>
          </div>
        </div>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.reservationsDistance') }}
        </label>

        <select
          slot="field"
          v-model="reservationOvertake">
          <option :value="-1">{{ $t('settingsReservations.nextDay') }}</option>
          <option :value="0">{{ $t('common.ui.none') }}</option>
          <option :value="30">{{ $t('settingsReservations.minutes30') }}</option>
          <option :value="60">{{ $t('settingsReservations.hours1') }}</option>
          <option :value="90">{{ $t('settingsReservations.hours1minutes30') }}</option>
          <option :value="120">{{ $t('settingsReservations.hours2') }}</option>
          <option :value="150">{{ $t('settingsReservations.hours2minutes30') }}</option>
          <option :value="180">{{ $t('settingsReservations.hours3') }}</option>
          <option :value="210">{{ $t('settingsReservations.hours3minutes30') }}</option>
          <option :value="240">{{ $t('settingsReservations.hours4') }}</option>
          <option :value="270">{{ $t('settingsReservations.hours4minutes30') }}</option>
          <option :value="300">{{ $t('settingsReservations.hours5') }}</option>
          <option :value="330">{{ $t('settingsReservations.hours5minutes30') }}</option>
          <option :value="360">{{ $t('settingsReservations.hours6') }}</option>
          <option :value="720">{{ $t('settingsReservations.hours12') }}</option>
          <option :value="1440">{{ $t('settingsReservations.hours24') }}</option>
          <option :value="2880">{{ $t('settingsReservations.hours48') }}</option>
          <option :value="4320">{{ $t('settingsReservations.hours72') }}</option>
          <option :value="7200">{{ $t('settingsReservations.days5') }}</option>
          <option :value="10080">{{ $t('settingsReservations.days7') }}</option>
          <option :value="14400">{{ $t('settingsReservations.days10') }}</option>
          <option :value="20160">{{ $t('settingsReservations.days14') }}</option>
        </select>
      </field>

      <field
        slot="content"
        type="small-field">
        <label slot="label">
          {{ $t('settingsReservations.allowFutureReservationsAtMax') }}
        </label>
        <input
          slot="field"
          v-model="maxReservationOvertake"
          type="number"
          size="10"
          class="max-reservation-overtake">
          <span slot="field">{{ $t('settingsReservations.daysForward') }}</span>
        <small
          class="note"
          slot="note">
          {{ $t('settingsReservations.zeroMeansNoLimits') }}
        </small>
      </field>

      <field
        slot="content"
        type="small-field">
        <label slot="label">
          {{ $t('settingsReservations.allowReservationCancellationAtMax') }}
        </label>
        <input
          slot="field"
          v-model="hoursToCancel"
          type="number"
          size="10"
          class="hours-to-cancel">
          <span slot="field">{{ $t('settingsReservations.hoursBefore') }}</span>
        <small
          class="note"
          slot="note">
          {{ $t('settingsReservations.zeroMeansNoLimits') }}
        </small>
      </field>

      <field
        v-if="hoursToCancel > 0"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.textDisplayedWhenCannotCancel') }}
        </label>

        <textarea
          slot="field"
          v-model="hoursToCancelText[currentLanguages.hoursToCancelText]" />

        <language-switcher
          slot="field"
          relatedTo="hoursToCancelText" />
      </field>
    </block>

    <block
      :title="$t('settingsReservations.additionalReservationsFeaturesTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field
        v-if="configLoaded && this.$store.state.config.permissions['periodicity']"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.periodicity') }}
        </label>

        <switcher
          v-model="periodicity"
          key="service-periodicity-switcher"
          slot="field" />
      </field>

      <field
        v-if="configLoaded && this.$store.state.config.permissions['periodicity'] && periodicity"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.allowPeriodicityCancel') }}
        </label>

        <switcher
          v-model="allowPeriodicityCancel"
          key="allow-periodicity-cancel-switcher"
          slot="field" />

        <small
          slot="note"
          class="note">
          {{ $t('settingsReservations.allowPeriodicityCancelDesc') }}
        </small>
      </field>

      <field
        v-if="configLoaded && this.$store.state.config.permissions['commissions']"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.commissions') }}
        </label>

        <switcher
          v-model="commissions"
          key="service-commmissions-switcher"
          slot="field"
          :disabled="!paymentsEnabled || !workersEnabled" />

        <small
          v-if="!paymentsEnabled"
          slot="note"
          class="note">
          {{ $t('settingsReservations.thisOptionRequiresPaymentsEnabled') }}
        </small>

        <small
          v-if="!workersEnabled"
          slot="note"
          class="note">
          {{ $t('settingsReservations.thisOptionRequiresWorkersEnabled') }}
        </small>
      </field>

      <field
        v-if="configLoaded && this.$store.state.config.permissions['passes']"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.passes') }}
          <span class="beta-badge">BETA</span>
        </label>

        <switcher
          v-model="passes"
          key="service-passes-switcher"
          slot="field"
          :disabled="!paymentsEnabled || customersBasedOn === 'no_customers'" />

        <small
          v-if="!paymentsEnabled"
          slot="note"
          class="note">
          {{ $t('settingsReservations.thisOptionRequiresPaymentsEnabled') }}
        </small>

        <small
          v-if="customersBasedOn === 'no_customers'"
          slot="note"
          class="note">
          {{ $t('settingsReservations.thisOptionRequiresCustomersManagementEnabled') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.enableWaitingList') }}
        </label>

        <switcher
          v-model="waitingList"
          key="waiting-list-switcher"
          slot="field" />
      </field>

      <field
        v-if="waitingList"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.autoWaitingList') }}
        </label>

        <switcher
          v-model="autoWaitingList"
          key="auto-waiting-list-switcher"
          slot="field" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.enableAttendancesList') }}
        </label>

        <switcher
          v-model="attendancesList"
          key="attendances-list-switcher"
          slot="field" />
      </field>

      <field
        v-if="attendancesList"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.autoAttendances') }}
        </label>

        <switcher
          v-model="autoAttendances"
          key="auto-attendances-switcher"
          slot="field" />
      </field>

      <field
        v-if="configLoaded && this.$store.state.config.permissions['discounts']"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.enableDiscountCodes') }}
        </label>

        <switcher
          v-model="paymentsDiscounts"
          slot="field"
          key="payment-discounts-switcher"
          :disabled="!servicesEnabled || !paymentsEnabled" />
        <small
          v-if="!servicesEnabled || !paymentsEnabled"
          slot="note"
          class="note">
          {{ $t('settingsReservations.thisOptionRequiresServicesAndPaymentsEnabled') }}
        </small>
      </field>
    </block>

    <block
      :title="$t('settingsReservations.additionalReservationsRulesTitle')"
      :isLoading="!configLoaded"
      :isSaving="saveInProgress">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.customersBasedOn') }}
        </label>

        <select
          slot="field"
          v-model="customersBasedOn">
          <option value="email">{{ $t('settingsReservations.emailAddress') }}</option>
          <option value="phone">{{ $t('settingsReservations.phoneNumber') }}</option>
          <option value="no_customers">{{ $t('settingsReservations.doNotManageCustomers') }}</option>
        </select>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.requireEmail') }}
        </label>

        <switcher
          v-model="requireMail"
          key="require-email-switcher"
          :disabled="customersBasedOn === 'email'"
          slot="field" />

        <small
          v-if="customersBasedOn === 'email'"
          class="note"
          slot="note">
          {{ $t('settingsReservations.youCannotDisableThisOptionIfCustomersAreBasedOnEmail') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.requirePhone') }}
        </label>

        <switcher
          v-model="requirePhone"
          key="require-phone-switcher"
          :disabled="customersBasedOn === 'phone'"
          slot="field" />

        <small
          v-if="customersBasedOn === 'phone'"
          class="note"
          slot="note">
          {{ $t('settingsReservations.youCannotDisableThisOptionIfCustomersAreBasedOnPhone') }}
        </small>
      </field>

      <field
        v-if="!requirePhone"
        slot="content">
        <label slot="label">
          {{ $t('settingsReservations.hidePhoneField') }}
        </label>

        <switcher
          v-model="hidePhoneField"
          key="hide-phone-field-switcher"
          slot="field" />
      </field>

      <field
        v-if="!hidePhoneField"
        slot="content"
        type="small-field">
        <label slot="label">
          {{ $t('settingsReservations.phonePrefix') }}
        </label>
        <input
          slot="field"
          v-model="phonePrefix"
          size="10"
          class="phone-prefix">
        <small
          class="note"
          slot="note">
          {{ $t('settingsReservations.phonePrefixDesc') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.requirePeopleNumber') }}
        </label>

        <switcher
          v-model="peopleRequired"
          key="people-required-switcher"
          slot="field" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsReservations.doNotAllowMoreThanOneReservationForTheSameTermAddedByOneUser') }}
        </label>

        <switcher
          v-model="blockUserReservation"
          key="block-user-reservation-switcher"
          slot="field" />
      </field>
    </block>
  </div>
</template>

<script>
import Block from './../common/Block';
import Configuration from './../mixins/Configuration';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Languages from './../mixins/Languages';
import SettingsBasicBlock from './../ui/SettingsBasicBlock';
import StickyHeader from './../common/StickyHeader';

export default {
  name: 'settings-reservations',
  mixins: [
    Configuration,
    Languages
  ],
  components: {
    'block': Block,
    'settings-basic-block': SettingsBasicBlock,
    'sticky-header': StickyHeader
  },
  computed: {
    timeIncreaser () {
      if (this.servicesEnabled && this.timeSpacesTheSameAsServiceDuration) {
        return -1;
      }

      return parseInt(this.timeSpaces.hours, 10) * 60 + parseInt(this.timeSpaces.minutes, 10);
    }
  },
  data () {
    return {
      configLoaded: false,
      errors: [],
      serviceName: '',
      hoursToCancelText: '',
      hoursToCancel: 0,
      showWorkers: false,
      startCalendarBy: 0,
      reservationOvertake: -1,
      maxReservationOvertake: 0,
      requireMail: false,
      requirePhone: false,
      hidePhoneField: false,
      phonePrefix: '',
      peopleRequired: false,
      commissions: false,
      passes: false,
      periodicity: false,
      allowPeriodicityCancel: true,
      attendancesList: false,
      autoAttendances: false,
      waitingList: false,
      autoWaitingList: false,
      customersBasedOn: 'no_customers',
      blockUserReservation: false,
      paymentsDiscounts: false,
      translations: {},
      currentLanguages: {
        service_name: '',
        hoursToCancelText: ''
      },
      saveInProgress: false,
      timeSpacesTheSameAsServiceDuration: false,
      timeSpaces: {
        hours: 0,
        minutes: 0
      }
    };
  },
  watch: {
    'customersBasedOn': function (newValue) {
      if (newValue === 'email') {
        this.requireMail = true;
      }

      if (newValue === 'phone') {
        this.requirePhone = true;
      }

      if (newValue === 'no_customers' && this.passes) {
        this.passes = false;

        this.$bus.$emit('alert-display', {
          title: this.$t('settingsReservations.passesDisabledTitle'),
          desc: this.$t('settingsReservations.passesDisabledDesc'),
          okLabel: this.$t('common.ui.ok'),
          okClick: () => true,
          isDanger: true
        });
      }
    }
  },
  mounted () {
    if (this.$store.state.config.user.id !== 0) {
      this.configurationLoad();
    }

    this.$bus.$on('configuration-loaded', this.configurationLoad);
  },
  methods: {
    configurationLoad () {
      this.configureMultilanguage();
      this.loadTranslations();
    },
    configureMultilanguage () {
      this.currentLanguages.service_name = this.defaultLanguageCode;
      this.currentLanguages.hoursToCancelText = this.defaultLanguageCode;
      this.$bus.$on('change-field-language', this.changeCurrentLanguageForField);
    },
    loadTranslations () {
      this.$http.post('/panel/general/get-configuration-translations').then(response => {
        this.translations = response.data;

        if (!Object.keys(this.translations).length) {
          this.translations = this.createEmptyTranslationsObject(['service_name', 'hours_to_cancel_text']);
        }

        this.setFormData();
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/general/get-configuration-translations', this.$router, this.$bus);
      });
    },
    setFormData () {
      this.serviceName = this.getLanguageFieldValues(
        this.translations,
        'service_name',
        this.$store.state.config.company.service_name
      );

      this.hoursToCancelText = this.getLanguageFieldValues(
        this.translations,
        'hours_to_cancel_text',
        this.$store.state.config.company.hours_to_cancel_text
      );

      this.showWorkers = !!this.$store.state.config.company.show_workers;

      if (parseInt(this.$store.state.config.company.time_increaser, 10) === -1 && this.servicesEnabled) {
        this.timeSpaces.hours = 0;
        this.timeSpaces.minutes = 0;
        this.timeSpacesTheSameAsServiceDuration = true;
      } else {
        this.timeSpaces.hours = Math.floor(this.$store.state.config.company.time_increaser / 60);
        this.timeSpaces.minutes = this.$store.state.config.company.time_increaser % 60;
        this.timeSpacesTheSameAsServiceDuration = false;
      }

      this.startCalendarBy = this.$store.state.config.company.start_calendar_by;
      this.reservationOvertake = this.$store.state.config.company.reservation_overtake;
      this.maxReservationOvertake = this.$store.state.config.company.max_reservation_overtake;
      this.hoursToCancel = this.$store.state.config.company.hours_to_cancel;
      this.requireMail = !!this.$store.state.config.company.require_mail;
      this.requirePhone = !!this.$store.state.config.company.require_phone;
      this.hidePhoneField = !!this.$store.state.config.company.hide_phone_field;
      this.phonePrefix = this.$store.state.config.company.phone_prefix;
      this.peopleRequired = !!this.$store.state.config.company.people_required;
      this.passes = !!this.$store.state.config.company.passes;
      this.commissions = !!this.$store.state.config.company.commissions;
      this.periodicity = !!this.$store.state.config.company.periodicity;
      this.allowPeriodicityCancel = !this.$store.state.config.company.disable_periodicity_cancel;
      this.waitingList = !!this.$store.state.config.company.waiting_list;
      this.autoWaitingList = !!this.$store.state.config.company.auto_waiting_list;
      this.attendancesList = !!this.$store.state.config.company.attendances_list;
      this.autoAttendances = !!this.$store.state.config.company.auto_attendances;
      this.customersBasedOn = this.$store.state.config.company.customers_based_on;
      this.blockUserReservation = !!this.$store.state.config.company.block_user_reservation;
      this.paymentsDiscounts = !!this.$store.state.config.company.discounts;
      this.configLoaded = true;

      if (this.requirePhone) {
        this.hidePhoneField = false;
      }
    },
    validate () {

    },
    save () {
      this.validate();
      this.saveInProgress = true;

      if (this.errors.length) {
        this.$bus.$emit('notification-display', {
          message: this.$t('common.ui.invalidFields'),
          type: 'error'
        });

        return;
      }

      this.saveForm();
    },
    saveForm () {
      let formData = this.prepareFormData();
      formData.settings = 'reservations';

      this.$http.post('/panel/settings/settings-save', formData).then(response => {
        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsReservations.reservationSettingsSaved'),
            type: 'success'
          });

          this.$store.commit('config/setCompanyPartialConfig', this.prepareFormData());
          this.$router.push('/settings');
          this.saveInProgress = false;
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/settings/settings-save', this.$router, this.$bus, this.saveForm.bind(this));
        this.saveInProgress = false;
      });
    },
    prepareFormData () {
      if (this.requirePhone) {
        this.hidePhoneField = false;
      }

      let formData = {
        service_name: this.serviceName[this.defaultLanguageCode],
        show_workers: this.showWorkers,
        time_increaser: this.timeIncreaser,
        start_calendar_by: this.startCalendarBy,
        reservation_overtake: this.reservationOvertake,
        max_reservation_overtake: this.maxReservationOvertake,
        hours_to_cancel: this.hoursToCancel,
        hours_to_cancel_text: this.hoursToCancelText[this.defaultLanguageCode],
        require_mail: this.requireMail,
        require_phone: this.requirePhone,
        hide_phone_field: this.hidePhoneField,
        phone_prefix: this.phonePrefix,
        people_required: this.peopleRequired,
        periodicity: this.periodicity,
        disable_periodicity_cancel: +!this.allowPeriodicityCancel,
        commissions: this.commissions,
        passes: this.passes,
        attendances_list: this.attendancesList,
        auto_attendances: this.autoAttendances,
        waiting_list: this.waitingList,
        auto_waiting_list: this.autoWaitingList,
        customers_based_on: this.customersBasedOn,
        block_user_reservation: this.blockUserReservation,
        discounts: this.paymentsDiscounts
      };

      if (this.languagesAreAvailable) {
        formData.translations = this.prepareTranslationsObject({
          service_name: this.serviceName,
          hours_to_cancel_text: this.hoursToCancelText
        });
      }

      return formData;
    }
  },
  beforeDestroy () {
    this.$bus.$off('change-field-language', this.changeCurrentLanguageForField);
    this.$bus.$off('configuration-loaded', this.setFormData);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.settings-reservations {
  padding: 0 0 2rem 0;
  width: 100%;

  .hours-to-cancel,
  .max-reservation-overtake {
    margin-right: 1rem;
    text-align: right;
  }

  .field-flex {
    align-items: center;
    display: flex;
    padding-bottom: 1rem;

    small {
      padding-left: 1rem;
    }
  }

  select[name="time-spaces-hours"],
  select[name="time-spaces-minutes"] {
    width: 100px;
  }

  /deep/ .beta-badge {
    background: $color-danger;
    border-radius: $border-radius-small;
    color: $color-light;
    display: inline;
    font-size: 1.2rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1;
    margin-left: .5rem;
    padding: 2px 4px;
    position: relative;
    top: -1px;
  }
}

@media (max-width: 640px) {
  .settings-reservations {
    margin-top: 5rem;
  }
}

@media (max-width: 580px) {
  .settings-reservations {
    .field {
      label,
      input:not(.max-reservation-overtake),
      select,
      textarea {
        text-align: left;
        width: calc(100% - 50px);
      }

      label {
        margin-bottom: .5rem;
      }

      small.note {
        padding: .5rem 0 0 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/SettingsReservations.vue